/*

  pageGataDagsins.ts

  Single page UI for Gáta Dagsins using the Mithril library

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

  This UI is built on top of Mithril (https://mithril.js.org), a lightweight,
  straightforward JavaScript single-page reactive UI library.

  The page is structured into models, actions and views,
  cf. https://github.com/pakx/the-mithril-diaries/wiki/Basic-App-Structure

*/

import { GlobalState } from "./globalstate";
import { m } from "./mithril";
import { View } from "./view";
import { Model, getSettings } from "./model";
import { Actions } from "./actions";
import { GataDagsins } from "./gatadagsins";
import { IView } from "./types";

export type LoginResult = "success" | "expired" | "error";

export async function main(state: GlobalState, container: HTMLElement): Promise<LoginResult> {
  // The main UI entry point, called from page.html
  if (!container) {
    console.error("No container element found");
    return "error";
  }
  try {
    // Skip initial login - authentication will happen lazily on first API call
    // Create the model, view and actions objects
    const settings = getSettings();
    const model = new Model(settings, state);
    const actions = new Actions(model);
    const view: IView = new View(actions);

    // Get date from URL parameter, fallback to today
    const urlParams = new URLSearchParams(window.location.search);
    const dateParam = urlParams.get('date');
    const today = new Date().toISOString().split("T")[0];
    const riddleDate = dateParam || today;

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    const validDate = dateRegex.test(riddleDate) ? riddleDate : today;

    const locale = state.locale || "is_IS";

    // Log the date being used (helpful for debugging)
    if (dateParam) {
      // console.log(`Loading Gáta Dagsins for date: ${validDate} (from URL parameter)`);
    }

    // Mount the Gáta Dagsins UI using an anonymous closure component
    m.mount(container, {
      view: () => m(GataDagsins, { view, date: validDate, locale }),
    });
  } catch(e) {
    console.error("Exception during initialization: ", e);
    return "error";
  }
  return "success";
}

export function unmount(container: HTMLElement) {
  // Unmount the Mithril UI
  m.mount(container, null);
}
